import { Node, PropertyOrKeyValue } from "../../Node";
import { PNumber } from "../../properties/PNumber";
import { PBoolean } from "../../properties/PBoolean";
import { PThreeConstant } from "../../properties/PThreeConstant";
import Stats from "stats.js";
import React from "react";
import * as THREE from "three";

export class RendererNode extends Node {
  ShadowsEnabled: boolean;
  Shadows: boolean;
  ToneMapping: string;
  ToneMappingExposure: number;
  ShadowMapType: string;
  antialias: boolean;
  FPS: boolean;
  Efficient: boolean;
  stats: Stats & { beginCallback: any; endCallback: any };
  rendererInfo: Element;

  get renderer() {
    return APP.renderer;
  }

  createProperties() {
    return [
      new PThreeConstant({
        name: "ToneMapping",
        default: "NoToneMapping",
        comment:
          "These define the WebGLRenderer's toneMapping property. This is used to approximate the appearance of high dynamic range (HDR) on the low dynamic range medium of a standard computer monitor or mobile device's screen.",
        selectOptions: [
          "NoToneMapping",
          "LinearToneMapping",
          "ReinhardToneMapping",
          "CineonToneMapping",
          "ACESFilmicToneMapping",
        ],
        node: this,
      }),
      new PNumber({
        name: "ToneMappingExposure",
        default: 1,
        min: 0,
        max: 10,
        comment: "Exposure level of tone mapping. Default is 1.",
        showInEditor: (p) => p.node.ToneMapping != "NoToneMapping",
        node: this,
      }),
      {
        separator: true,
      },
      new PBoolean({
        name: "Shadows",
        comment: "can be used to turn off shadows",
        default: true,
        node: this,
      }),
      new PThreeConstant({
        name: "ShadowMapType",
        label: "ShadowMapType*",
        default: "PCFSoftShadowMap",
        comment:
          "Defines shadow map type (unfiltered, percentage close filtering, percentage close filtering with bilinear filtering in shader)\n" +
          "BasicShadowMap gives unfiltered shadow maps - fastest, but lowest quality.\n" +
          "PCFShadowMap filters shadow maps using the Percentage-Closer Filtering (PCF) algorithm (default).\n" +
          "PCFSoftShadowMap filters shadow maps using the Percentage-Closer Filtering (PCF) algorithm with better soft shadows especially when using low-resolution shadow maps.\n" +
          "VSMShadowMap filters shadow maps using the Variance Shadow Map (VSM) algorithm. When using VSMShadowMap all shadow receivers will also cast shadows.",
        selectOptions: ["BasicShadowMap", "PCFShadowMap", "PCFSoftShadowMap", "VSMShadowMap"],
        node: this,
        showInEditor: (p) => p.node.Shadows,
      }),
      {
        separator: true,
      },
      new PBoolean({
        name: "antialias",
        label: "Antialias*",
        comment: "whether to perform antialiasing",
        default: false,
        node: this,
      }),
      new PBoolean({
        name: "Efficient",
        label: "Lazy Frames",
        default: true,
        comment:
          "Rendert nur dann ein neues Bild, wenn notwendig, und senkt dadurch den Stromverbrauch des Rechners deutlich.",
        node: this,
      }),
      {
        separator: true,
      },
      new PBoolean({
        name: "FPS",
        label: "FPS anzeigen",
        default: false,
        comment:
          "zeigt eine kleine Info-Box mit Performance-Daten an. FPS Frames rendered in the last second. The higher the number the better. MS Milliseconds needed to render a frame. The lower the number the better. MB MBytes of allocated memory.",
        node: this,
      }),
      {
        separator: true,
      },
      {
        render: () =>
          React.createElement(
            "p",
            { className: "info" },
            `(*) wirken erst nach Speichern und Neu Laden der Seite`,
          ),
      },
    ] as PropertyOrKeyValue[];
  }

  init() {
    super.init();
    this.updateScene();
    return this;
  }

  updateScene() {
    this._dataChanged = {};
    if (this.changed("Shadows")) {
      this.renderer.shadowMap.enabled = this.Shadows;
      this.renderer.shadowMap.needsUpdate = true;
    }
    if (this.changed("ToneMapping") || this.changed("ToneMappingExposure")) {
      this.renderer.toneMapping = THREE[this.ToneMapping];
      this.renderer.toneMappingExposure = this.ToneMappingExposure;
    }
    if (this.changed("Efficient")) {
      STAGE.renderLooping = !this.Efficient;
      STAGE.dirty();
    }
    if (this.changed("FPS")) {
      if (this.stats) {
        document.body.removeChild(this.stats.dom);
        STAGE.off("beforeRenderScene", this.stats.beginCallback);
        STAGE.off("afterRenderScene", this.stats.endCallback);
        this.stats = null;
        document.body.removeChild(this.rendererInfo);
        // @ts-ignore
        STAGE.off("beforeRenderScene", this.rendererInfo.updateCallback);
        this.rendererInfo = null;
      }
      if (this.FPS) {
        this.stats = new Stats() as any;
        this.stats.beginCallback = () => this.stats.begin();
        this.stats.endCallback = () => this.stats.end();
        document.body.appendChild(this.stats.dom);
        STAGE.on("beforeRenderScene", this.stats.beginCallback);
        STAGE.on("afterRenderScene", this.stats.endCallback);
        this.rendererInfo = document.createElement("div");
        const fmt = (number) => new Intl.NumberFormat().format(number);
        // @ts-ignore
        this.rendererInfo.updateCallback = () => {
          const info = STAGE.renderer.info;
          let objectCount = 0;
          STAGE.scene.traverse((x) => objectCount++);
          // @ts-ignore
          this.rendererInfo.innerText = `Geometries: ${info.memory.geometries}
Objects: ${fmt(objectCount)}
Textures: ${fmt(info.memory.textures)}
Programs: ${fmt(info.programs.length)}
Calls: ${fmt(info.render.calls)}
Triangles: ${fmt(info.render.triangles)}
Points: ${fmt(info.render.points)}
Lines: ${fmt(info.render.lines)}`;
        };
        // @ts-ignore
        Object.assign(this.rendererInfo.style, {
          position: "absolute",
          left: "0px",
          bottom: "0px",
          zIndex: 10000,
          fontSize: "x-small",
          width: "10em",
          backgroundColor: "rgba(0,0,0,0.7)",
          padding: "4px",
          color: "cyan",
          whiteSpace: "pre",
        });
        document.body.appendChild(this.rendererInfo);
        // @ts-ignore
        STAGE.on("beforeRenderScene", this.rendererInfo.updateCallback);
      }
    }
  }

  getName() {
    return "Renderer";
  }

  saveSettings() {
    return APP.wkfgBackendApi.saveSettings([
      {
        Key: "stage.renderer.antialias",
        Value: "" + this.antialias,
        Type: "Bool",
        Comment: "Wird der Renderer mit Kantenglättung verwendet?",
      },
      {
        Key: "stage.shadows.enabled",
        Value: "" + this.Shadows,
        Type: "Bool",
        Comment: "Werden Schatten angezeigt?",
      },
      {
        Key: "stage.shadows.shadowMapType",
        Value: this.ShadowMapType,
        Type: "String",
        Comment: "Welche Art der ShadowMap wird verwendet?",
      },
    ]);
  }
}
